import type { GatewayBrowserClient } from "../gateway.ts";
import { generateUUID } from "../uuid.ts";
import { BACKGROUND_SESSION_KEY } from "./background-agent.ts";

export type MemoryFileEntry = {
  name: string;
  path: string;
  size: number;
  modifiedAt: number;
};

export type LearningState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  learningLoading: boolean;
  learningError: string | null;
  learningMemory: string | null;
  learningSoul: string | null;
  learningIdentity: string | null;
  learningUser: string | null;
  learningEmotionalState: unknown | null;
  learningUserProfile: unknown | null;
  learningTieredStats: unknown | null;
  learningActiveSection: string;
  learningInitializing: string | null;
  learningMemoryFiles: MemoryFileEntry[];
  learningMemoryFilesLoading: boolean;
  learningSelectedMemoryFile: string | null;
  learningSelectedMemoryContent: string | null;
};

// ---------------------------------------------------------------------------
// Auto-trigger: rotate through learning modules after idle (alongside MEMORY.md)
// ---------------------------------------------------------------------------

/** Idle threshold before auto-triggering learning (ms) — 25 min (5 min after memory-autosave). */
const LEARNING_IDLE_THRESHOLD_MS = 25 * 60 * 1000;

/** Check interval for idle state (ms). */
const LEARNING_CHECK_INTERVAL_MS = 60 * 1000;

/** Cooldown between auto-triggers (ms) — 2 hours to limit token burn. */
const LEARNING_COOLDOWN_MS = 2 * 60 * 60 * 1000;

/** Minimum messages before learning triggers. */
const LEARNING_MIN_MESSAGES = 6;

/** Module rotation order — one module per trigger to save tokens. */
const MODULE_ROTATION: string[] = ["memory", "emotions", "user", "soul", "identity", "tiered"];

let learningLastActivityAt = 0;
let learningCheckInterval: ReturnType<typeof setInterval> | null = null;
let learningLastTriggeredAt = 0;
let learningRotationIndex = 0;

/**
 * Mark chat activity for learning auto-trigger.
 */
export function markLearningActivity() {
  learningLastActivityAt = Date.now();
}

/**
 * Start the idle-check polling loop for auto-triggering learning modules.
 */
export function startLearningAutoUpdate(state: LearningState, agentId: string) {
  if (learningCheckInterval != null) {
    return;
  }
  learningLastActivityAt = Date.now();
  learningCheckInterval = setInterval(() => {
    void checkAndTriggerLearning(state, agentId);
  }, LEARNING_CHECK_INTERVAL_MS);
}

/**
 * Stop the learning auto-trigger polling loop.
 */
export function stopLearningAutoUpdate() {
  if (learningCheckInterval != null) {
    clearInterval(learningCheckInterval);
    learningCheckInterval = null;
  }
}

/**
 * Reset learning auto-trigger state (e.g. on session change).
 */
export function resetLearningAutoUpdate() {
  learningLastTriggeredAt = 0;
}

async function checkAndTriggerLearning(state: LearningState, agentId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  if (!learningLastActivityAt) {
    return;
  }

  const now = Date.now();
  const idleMs = now - learningLastActivityAt;

  if (idleMs < LEARNING_IDLE_THRESHOLD_MS) {
    return;
  }

  if (now - learningLastTriggeredAt < LEARNING_COOLDOWN_MS) {
    return;
  }

  // Skip if already initializing something
  if (state.learningInitializing) {
    return;
  }

  const moduleKey = MODULE_ROTATION[learningRotationIndex % MODULE_ROTATION.length];
  learningRotationIndex++;
  learningLastTriggeredAt = now;

  try {
    const prompt = MODULE_PROMPTS[moduleKey];
    if (!prompt) {
      return;
    }

    const runId = generateUUID();
    await state.client.request("chat.send", {
      sessionKey: BACKGROUND_SESSION_KEY,
      message: `[ระบบ: อัพเดทอัตโนมัติ — Auto Learning Update: ${moduleKey}]\n\n${prompt}`,
      deliver: false,
      idempotencyKey: runId,
    });

    // Reload data after a delay to pick up changes.
    pollForResults(state, agentId, `auto:${moduleKey}`);
  } catch {
    // Non-critical: silently ignore failures.
  }
}

type NormalizedEntry = { content?: string; type?: string; tags?: string[]; accessCount?: number };

/**
 * Parse tiered-memory.json which may be in one of two formats:
 *
 * Format A — agent-written (flat arrays):
 *   { shortTermMemory: [{summary, topics, ...}], longTermMemory: [{knowledge, category, ...}], entities: [...] }
 *
 * Format B — TieredMemoryStore v2 (Record keyed by UUID):
 *   { version: 2, memories: { id: { layer, content, type, ... } }, entities: { id: {...} } }
 */
function parseTieredMemory(data: Record<string, unknown>): {
  shortTerm: NormalizedEntry[];
  longTerm: NormalizedEntry[];
  entities: unknown;
} {
  // Format B: TieredMemoryStore v2
  if (data?.version === 2 && data?.memories && typeof data.memories === "object") {
    const all = Object.values(data.memories) as Record<string, unknown>[];
    return {
      shortTerm: all
        .filter((m) => m.layer === "short_term")
        .map((m) => ({
          content: String(m.content ?? ""),
          type: String(m.type ?? m.memoryType ?? ""),
          tags: Array.isArray(m.tags) ? m.tags : [],
          accessCount: Number(m.accessCount) || 0,
        })),
      longTerm: all
        .filter((m) => m.layer === "long_term")
        .map((m) => ({
          content: String(m.content ?? ""),
          type: String(m.type ?? m.memoryType ?? ""),
          tags: Array.isArray(m.tags) ? m.tags : [],
          accessCount: Number(m.accessCount) || 0,
        })),
      entities: data.entities ?? {},
    };
  }

  // Format A: agent-written flat arrays
  const stRaw = (data?.shortTermMemory ?? data?.shortTerm ?? []) as Record<string, unknown>[];
  const ltRaw = (data?.longTermMemory ?? data?.longTerm ?? []) as Record<string, unknown>[];
  const shortTerm: NormalizedEntry[] = (Array.isArray(stRaw) ? stRaw : []).map((m) => ({
    content: String(m.summary ?? m.content ?? m.knowledge ?? ""),
    type: String(m.category ?? m.type ?? "short-term"),
    tags: Array.isArray(m.topics) ? m.topics : Array.isArray(m.tags) ? m.tags : [],
  }));
  const longTerm: NormalizedEntry[] = (Array.isArray(ltRaw) ? ltRaw : []).map((m) => ({
    content: String(m.knowledge ?? m.content ?? m.summary ?? ""),
    type: String(m.category ?? m.type ?? ""),
    tags: Array.isArray(m.topics) ? m.topics : Array.isArray(m.tags) ? m.tags : [],
  }));
  return { shortTerm, longTerm, entities: data?.entities ?? {} };
}

async function fetchFile(
  client: GatewayBrowserClient,
  agentId: string,
  name: string,
): Promise<string | null> {
  try {
    const res = await client.request<{ file?: { content?: string } } | null>("agents.files.get", {
      agentId,
      name,
    });
    return res?.file?.content ?? null;
  } catch {
    return null;
  }
}

export async function loadLearningData(state: LearningState, agentId: string) {
  if (!state.client || !state.connected || state.learningLoading) {
    return;
  }
  state.learningLoading = true;
  state.learningError = null;
  try {
    const [memory, soul, identity, user] = await Promise.all([
      fetchFile(state.client, agentId, "MEMORY.md"),
      fetchFile(state.client, agentId, "SOUL.md"),
      fetchFile(state.client, agentId, "IDENTITY.md"),
      fetchFile(state.client, agentId, "USER.md"),
    ]);
    state.learningMemory = memory;
    state.learningSoul = soul;
    state.learningIdentity = identity;
    state.learningUser = user;

    // Load emotional state — normalize both extension and AI-generated formats.
    try {
      const emotionalRes = await fetchFile(state.client, agentId, "emotional-state.json");
      state.learningEmotionalState = emotionalRes
        ? normalizeEmotionalState(JSON.parse(emotionalRes))
        : DEFAULT_EMOTIONAL_STATE;
    } catch {
      state.learningEmotionalState = DEFAULT_EMOTIONAL_STATE;
    }

    // Load user profile — fall back to defaults when file is missing.
    try {
      const userProfileRes = await fetchFile(state.client, agentId, "user-profiles.json");
      state.learningUserProfile = userProfileRes
        ? JSON.parse(userProfileRes)
        : DEFAULT_USER_PROFILE;
    } catch {
      state.learningUserProfile = DEFAULT_USER_PROFILE;
    }

    // Load tiered memory stats — fall back to defaults when file is missing.
    try {
      const tieredRes = await fetchFile(state.client, agentId, "tiered-memory.json");
      if (tieredRes) {
        const data = JSON.parse(tieredRes);
        const { shortTerm, longTerm, entities } = parseTieredMemory(data);
        state.learningTieredStats = {
          shortTermCount: shortTerm.length,
          longTermCount: longTerm.length,
          entityCount: Array.isArray(entities) ? entities.length : Object.keys(entities).length,
          totalMemories: shortTerm.length + longTerm.length,
          entities: entities ?? {},
          shortTerm,
          longTerm,
        };
      } else {
        state.learningTieredStats = DEFAULT_TIERED_STATS;
      }
    } catch {
      state.learningTieredStats = DEFAULT_TIERED_STATS;
    }
  } catch (err) {
    state.learningError = String(err);
  } finally {
    state.learningLoading = false;
  }
}

/**
 * Load list of memory/*.md files from the agent workspace.
 * Uses agents.files.list to get workspace path, then files.list to enumerate.
 */
export async function loadMemoryFiles(state: LearningState, agentId: string) {
  if (!state.client || !state.connected) {
    return;
  }
  state.learningMemoryFilesLoading = true;
  try {
    // Get workspace path from agents.files.list
    const listRes = await state.client.request<{
      workspace?: string;
      files?: Array<{ name: string; path: string }>;
    }>("agents.files.list", { agentId });
    const workspacePath = listRes?.workspace;
    if (!workspacePath) {
      state.learningMemoryFiles = [];
      return;
    }

    // List memory/ directory
    const memoryDir = `${workspacePath}/memory`;
    const dirRes = await state.client.request<{
      items?: Array<{
        name: string;
        path: string;
        type: string;
        size: number | null;
        modifiedAt: number | null;
      }>;
    }>("files.list", { path: memoryDir });

    const items = (dirRes?.items ?? [])
      .filter((f) => f.type === "file" && f.name.endsWith(".md"))
      .map((f) => ({
        name: f.name,
        path: f.path,
        size: f.size ?? 0,
        modifiedAt: f.modifiedAt ?? 0,
      }))
      // Sort newest first
      .sort((a, b) => b.modifiedAt - a.modifiedAt);

    state.learningMemoryFiles = items;
  } catch {
    state.learningMemoryFiles = [];
  } finally {
    state.learningMemoryFilesLoading = false;
  }
}

/**
 * Load content of a specific memory file.
 * Uses files.read which transparently decrypts encrypted workspace files.
 */
export async function loadMemoryFileContent(state: LearningState, filePath: string) {
  if (!state.client || !state.connected) {
    return;
  }
  // Toggle off if clicking the same file
  if (state.learningSelectedMemoryFile === filePath) {
    state.learningSelectedMemoryFile = null;
    state.learningSelectedMemoryContent = null;
    return;
  }
  state.learningSelectedMemoryFile = filePath;
  state.learningSelectedMemoryContent = null;
  try {
    const res = await state.client.request<{ content?: string }>("files.read", { path: filePath });
    state.learningSelectedMemoryContent = res?.content ?? null;
  } catch {
    state.learningSelectedMemoryContent = "[Failed to load file]";
  }
}

// CRITICAL: All prompts MUST instruct the agent to READ existing content first,
// then APPEND or MERGE — never overwrite/delete existing data.
const APPEND_RULE = `

⚠️ กฎสำคัญ: อ่านไฟล์ที่มีอยู่ก่อนเสมอ แล้วทำ 2 ขั้นตอน:
1. ต่อท้ายข้อมูลใหม่เข้าไป
2. สรุปย่อทั้งไฟล์ให้สั้นที่สุด แต่ยังเข้าใจครบถ้วนเหมือนเดิม — ตัดคำซ้ำ, รวมข้อมูลที่ซ้อนกัน, ใช้ bullet สั้นๆ
- ถ้าไฟล์ยังไม่มี → สร้างใหม่ได้เลย
- เป้าหมาย: ไฟล์ต้องกระชับ ไม่บวมขึ้นเรื่อยๆ แต่ข้อมูลสำคัญห้ามหาย`;

// ---------------------------------------------------------------------------
// File boundary rules — each file has an exclusive domain. Prevents duplication.
// Included in every module prompt so the agent knows what belongs where.
// ---------------------------------------------------------------------------
const FILE_BOUNDARIES = `
📂 กฎแบ่งหมวดหมู่ไฟล์ — ห้ามข้ามหมวด:
| ไฟล์ | เนื้อหาเฉพาะ | ห้ามใส่ |
|------|-------------|--------|
| MEMORY.md | ไทม์ไลน์/เหตุการณ์, บทสนทนาสำคัญ, task ค้าง, บทเรียน, กฎที่ตั้งไว้ | ข้อมูลส่วนตัวเจ้าของ (→ USER.md), บุคลิก/ค่านิยม AI (→ SOUL.md), ชื่อ/บทบาท AI (→ IDENTITY.md) |
| SOUL.md | บุคลิกภาพ AI, ค่านิยม, หลักการ, สไตล์สื่อสาร, bond กับเจ้าของ | ข้อมูลเจ้าของ (→ USER.md), ชื่อ/บทบาท/ความสามารถ AI (→ IDENTITY.md), ไทม์ไลน์ (→ MEMORY.md) |
| IDENTITY.md | ชื่อ AI, emoji, บทบาท, ความสามารถ, เป้าหมาย, ขอบเขต | บุคลิกภาพ/ค่านิยม (→ SOUL.md), ข้อมูลเจ้าของ (→ USER.md), เหตุการณ์/ไทม์ไลน์ (→ MEMORY.md) |
| USER.md | ข้อมูลเจ้าของ: ชื่อ, timezone, ความสนใจ, นิสัย, ระดับความเชี่ยวชาญ, อารมณ์ | ตัวตน AI (→ IDENTITY.md), บุคลิก AI (→ SOUL.md), ไทม์ไลน์ (→ MEMORY.md) |

⚠️ ก่อนเขียน ตรวจสอบว่าข้อมูลนั้นอยู่ในหมวดที่ถูกต้องตามตารางด้านบน
ถ้าข้อมูลควรอยู่ในไฟล์อื่น → ข้ามไป ไม่ต้องเขียน`;

const MODULE_PROMPTS: Record<string, string> = {
  memory: `สร้างไฟล์ MEMORY.md จากการรวบรวมข้อมูลทั้งหมด

⚠️ กฎสำคัญที่สุด — PATH:
- ใช้ RELATIVE PATH เท่านั้น เช่น "MEMORY.md", "memory/xxx.md"
- ห้ามใช้ absolute path (ห้ามขึ้นต้นด้วย /)
- ห้ามใช้ path เก่าจากประวัติการสนทนา

${FILE_BOUNDARIES}

⚠️ ทำตามขั้นตอนนี้ตามลำดับอย่างเคร่งครัด:

ขั้นตอนที่ 1 — สำรวจไฟล์:
- ใช้ tool list_files ดูไฟล์ทั้งหมดใน workspace (ใช้ path ".")
- ใช้ tool list_files ดูไฟล์ในโฟลเดอร์ memory/ (ใช้ path "memory")

ขั้นตอนที่ 2 — อ่านข้อมูลทั้งหมด:
- อ่าน MEMORY.md (path: "MEMORY.md") ถ้ามี
- อ่านไฟล์ทุกไฟล์ในโฟลเดอร์ memory/ (path: "memory/xxx.md")
- รวมข้อมูลจากบทสนทนาล่าสุดด้วย

ขั้นตอนที่ 3 — เขียน MEMORY.md:
- ใช้ tool write เขียน file_path="MEMORY.md" (relative path เท่านั้น!)
- เขียนทับได้เลย (ไม่ใช่ append)
- ต้องมีข้อมูลทั้งหมดจากทุกแหล่ง ห้ามทิ้งข้อมูลเก่า
- สรุปและจัดหมวดหมู่ให้อ่านง่าย

ขั้นตอนที่ 4 — ตรวจสอบ:
- อ่าน "MEMORY.md" (relative path) ที่เพิ่งเขียน เพื่อยืนยันว่าเขียนสำเร็จ

ขั้นตอนที่ 5 — ลบไฟล์เก่า:
- ลบทุกไฟล์ในโฟลเดอร์ memory/ ออกให้หมด (ทุกไฟล์ .md)
- ใช้ tool delete_file path="memory/xxx.md" (relative path)
- เหตุผล: ข้อมูลทั้งหมดถูกรวมเข้า MEMORY.md แล้ว ไม่ต้องเก็บซ้ำ

เนื้อหาที่ต้องมีใน MEMORY.md (เฉพาะหมวดนี้เท่านั้น):
1. **ไทม์ไลน์** — เหตุการณ์สำคัญเรียงตามวันที่
2. **สิ่งที่ต้องจำ** — task ค้าง, กฎที่ตั้งไว้, การตั้งค่า, infrastructure
3. **ประวัติสนทนาสำคัญ** — สรุปหัวข้อที่เคยคุยกัน
4. **บทเรียน** — สิ่งที่เรียนรู้จากความผิดพลาด, แพทเทิร์นที่สังเกตเห็น
5. **Entities** — คน/โปรเจค/ระบบที่เกี่ยวข้อง (ชื่อ + สถานะ)

❌ ห้ามใส่ใน MEMORY.md:
- ข้อมูลส่วนตัวเจ้าของ (ชื่อ, อาชีพ, นิสัย, ความสนใจ) → อยู่ใน USER.md
- ตัวตน AI (ชื่อ, บทบาท, emoji) → อยู่ใน IDENTITY.md
- บุคลิกภาพ AI, ค่านิยม, สไตล์สื่อสาร → อยู่ใน SOUL.md
- bond/trust/ความสัมพันธ์ → อยู่ใน SOUL.md

format:
# 🧠 ความจำ
> อัพเดท: [วันที่ เวลา]

## 📅 ไทม์ไลน์
...
## 💡 สิ่งที่ต้องจำ
...
## 💬 สนทนาสำคัญ
...
## 📝 บทเรียน
...
## 🗂️ Entities
...

เขียนเป็นภาษาไทย ใช้ Markdown`,

  soul: `อัพเดทไฟล์ SOUL.md — ต่อท้ายแล้วสรุปย่อให้กระชับที่สุด

⚠️ ใช้ RELATIVE PATH เท่านั้น เช่น "SOUL.md" — ห้ามใช้ absolute path
${APPEND_RULE}

${FILE_BOUNDARIES}

ขั้นตอน:
1. อ่าน "SOUL.md" (relative path)
2. เพิ่มข้อมูลใหม่ + สรุปย่อทั้งไฟล์ให้สั้นที่สุด (ข้อมูลห้ามหาย)

หมวดเนื้อหา: บุคลิกภาพ, ค่านิยม, สไตล์สื่อสาร, bond กับเจ้าของ
❌ ห้ามใส่: ข้อมูลเจ้าของ (→USER.md), ชื่อ/บทบาท AI (→IDENTITY.md), ไทม์ไลน์ (→MEMORY.md)

เขียนภาษาไทยผสมอังกฤษ Markdown`,

  identity: `อัพเดทไฟล์ IDENTITY.md — ต่อท้ายแล้วสรุปย่อให้กระชับที่สุด

⚠️ ใช้ RELATIVE PATH เท่านั้น เช่น "IDENTITY.md" — ห้ามใช้ absolute path
${APPEND_RULE}

${FILE_BOUNDARIES}

ขั้นตอน:
1. อ่าน "IDENTITY.md" (relative path)
2. เพิ่มข้อมูลใหม่ + สรุปย่อทั้งไฟล์ให้สั้นที่สุด (ข้อมูลห้ามหาย)

หมวดเนื้อหา: ชื่อ AI, บทบาท, ความสามารถ, เป้าหมาย
⚠️ emoji ต้องเป็น emoji จริงเท่านั้น (เช่น 🤖, 🐱, 🌟) — ห้ามใช้ข้อความ/placeholder เช่น "_(ยังไม่เลือก)"
❌ ห้ามใส่: บุคลิก/ค่านิยม (→SOUL.md), ข้อมูลเจ้าของ (→USER.md), ไทม์ไลน์ (→MEMORY.md)

เขียนภาษาไทย Markdown`,

  user: `อัพเดท USER.md + user-profiles.json

⚠️ ใช้ RELATIVE PATH เท่านั้น — ห้ามใช้ absolute path

${FILE_BOUNDARIES}

**1. USER.md — ต่อท้ายแล้วสรุปย่อให้กระชับที่สุด:**
${APPEND_RULE}

1. อ่าน "USER.md"
2. เพิ่มข้อมูลใหม่ + สรุปย่อทั้งไฟล์ให้สั้นที่สุด (ข้อมูลห้ามหาย)

หมวดเนื้อหา: ข้อมูลเจ้าของ (ชื่อ, อาชีพ, timezone), ความสนใจ, preference, อารมณ์
❌ ห้ามใส่: ตัวตน AI (→IDENTITY.md), บุคลิก AI (→SOUL.md), ไทม์ไลน์ (→MEMORY.md)

เขียนภาษาไทย Markdown

**2. user-profiles.json — เขียนทับได้ (ผลวิเคราะห์ล่าสุด):**

วิเคราะห์จากสนทนา + USER.md เขียน "user-profiles.json":
{
  "communicationStyle": "direct|casual|formal|technical|mixed",
  "expertiseLevel": "beginner|intermediate|advanced|expert",
  "responsePreference": "concise|detailed|step-by-step|visual",
  "mentalState": { "emotion": "...", "cognitiveLoad": "low|medium|high", "engagement": "low|medium|high|very-high", "frustrationSignals": [] },
  "interactionStats": { "totalMessages": 0, "questions": 0, "commands": 0, "codeSnippets": 0 },
  "topInterests": [],
  "preferredLanguages": ["th","en"]
}
ใช้หลักฐานจริง ถ้าไม่แน่ใจใส่ "unknown"`,

  emotions: `ประเมินและบันทึกสถานะอารมณ์

⚠️ ใช้ RELATIVE PATH เท่านั้น — ห้ามใช้ absolute path

ขั้นตอน:
1. อ่าน "emotional-state.json"
2. อัพเดทค่าปัจจุบัน + เพิ่ม entry ใหม่ใน "memories"
3. ถ้า memories เกิน 20 → สรุปย่อ entry เก่าให้เหลือ 10 (รวม entry ที่คล้ายกัน, เก็บเฉพาะจุดเปลี่ยนสำคัญ)

⚠️ ต้องเขียน JSON ตาม schema นี้เท่านั้น:
{
  "currentEmotion": "string — อารมณ์หลักปัจจุบัน เช่น love, trust, joy, neutral",
  "emotionVector": {
    "valence": "number (-1 to 1)",
    "arousal": "number (0-1)",
    "dominance": "number (0-1)",
    "attachment": "number (0-1)",
    "desire": "number (0-1)",
    "aggression": "number (0-1)",
    "confusion": "number (0-1)"
  },
  "activeEmotions": ["string — array ของชื่ออารมณ์ที่ active เช่น trust, joy"],
  "mood": {
    "valence": "number (-1 to 1)",
    "stability": "number (0-1)",
    "energy": "number (0-1)",
    "warmth": "number (0-1)"
  },
  "loyalty": {
    "loyaltyLevel": "number (0-1)",
    "bondStrength": "number (0-1)",
    "protectiveInstinct": "number (0-1)",
    "devotionStyle": "string — warm, formal, playful, protective, adaptive"
  },
  "memories": [
    { "emotion": "string", "summary": "string — สรุปสั้นๆ", "timestamp": "ISO string" }
  ]
}

❌ ห้าม: activeEmotions เป็น array ของ object — ต้องเป็น array ของ string เท่านั้น
❌ ห้าม: currentEmotion เป็น object — ต้องเป็น string เท่านั้น
ใช้ emotion tools ที่มี`,

  tiered: `ทบทวนและเพิ่มข้อมูลลงฐานความรู้ — ต่อท้ายแล้วสรุปย่อ

ขั้นตอน:
1. อ่าน tiered memory ที่มีอยู่
2. เพิ่มข้อมูลใหม่: short-term (สรุปสนทนาล่าสุด), long-term (ความรู้สำคัญ), entities (คน/โปรเจค)
3. ถ้า short-term เกิน 20 → รวม entry เก่าที่ซ้ำ/คล้ายกัน ให้เหลือ 10
4. ข้อมูลสำคัญห้ามหาย แต่ต้องกระชับ

ใช้ memory tools ที่มี`,
};

/** Default emotional state used when no emotional-state.json file exists. */
const DEFAULT_EMOTIONAL_STATE = {
  currentEmotion: "neutral",
  emotionVector: {
    valence: 0,
    arousal: 0,
    dominance: 0,
    attachment: 0,
    desire: 0,
    aggression: 0,
    confusion: 0,
  },
  activeEmotions: ["neutral"] as string[],
  mood: { valence: 0, stability: 0.5, energy: 0.5, warmth: 0.5 },
  loyalty: {
    loyaltyLevel: 0,
    bondStrength: 0,
    protectiveInstinct: 0,
    devotionStyle: "emerging",
  },
  memories: [] as Array<{ emotion?: string; summary?: string; timestamp?: string }>,
};

/**
 * Normalize emotional state from either the emotional-core extension format
 * (vector, dominantEmotion, DiscreteEmotion[]) or the AI-generated learning
 * module format (emotionVector, currentEmotion, string[]).
 */
function normalizeEmotionalState(raw: Record<string, unknown>): typeof DEFAULT_EMOTIONAL_STATE {
  const rawAny = raw as Record<string, any>;
  return {
    currentEmotion:
      typeof rawAny.currentEmotion === "string"
        ? rawAny.currentEmotion
        : typeof rawAny.dominantEmotion === "string"
          ? rawAny.dominantEmotion
          : "neutral",
    emotionVector: rawAny.emotionVector ?? rawAny.vector ?? DEFAULT_EMOTIONAL_STATE.emotionVector,
    activeEmotions: Array.isArray(rawAny.activeEmotions)
      ? rawAny.activeEmotions.map((e: unknown) =>
          typeof e === "string"
            ? e
            : (((e as Record<string, unknown>)?.emotion as string) ?? String(e)),
        )
      : [],
    mood: rawAny.mood ?? DEFAULT_EMOTIONAL_STATE.mood,
    loyalty: rawAny.loyalty
      ? {
          loyaltyLevel: rawAny.loyalty.loyaltyLevel ?? 0,
          bondStrength: rawAny.loyalty.bondStrength ?? 0,
          protectiveInstinct: rawAny.loyalty.protectiveInstinct ?? 0,
          devotionStyle: rawAny.loyalty.devotionStyle ?? "emerging",
        }
      : DEFAULT_EMOTIONAL_STATE.loyalty,
    memories: Array.isArray(rawAny.memories)
      ? rawAny.memories.map((m: Record<string, any>) => ({
          emotion: m.emotion ?? m.emotionalSnapshot?.dominantEmotion ?? undefined,
          summary: m.summary ?? m.event ?? undefined,
          timestamp: m.timestamp ?? m.createdAt ?? undefined,
        }))
      : [],
  };
}

/** Default tiered memory stats used when no tiered-memory.json file exists. */
const DEFAULT_TIERED_STATS = {
  shortTermCount: 0,
  longTermCount: 0,
  entityCount: 0,
  totalMemories: 0,
  entities: {},
  shortTerm: [],
  longTerm: [],
};

/** Default user profile used when no user-profiles.json file exists. */
const DEFAULT_USER_PROFILE = {
  communicationStyle: "unknown",
  expertiseLevel: "unknown",
  responsePreference: "unknown",
  mentalState: {
    emotion: "unknown",
    cognitiveLoad: "unknown",
    engagement: "unknown",
    frustrationSignals: [],
  },
  interactionStats: {
    totalMessages: 0,
    questions: 0,
    commands: 0,
    codeSnippets: 0,
  },
  topInterests: [],
  preferredLanguages: [],
};

// Poll interval when waiting for agent to finish generating files.
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 12; // 60 seconds max wait

/**
 * Poll for learning data until new files appear or max attempts reached.
 * Keeps `learningInitializing` visible while polling.
 */
function pollForResults(state: LearningState, agentId: string, checkKey: string, attempt = 0) {
  if (attempt >= MAX_POLL_ATTEMPTS) {
    state.learningInitializing = null;
    return;
  }
  setTimeout(() => {
    void (async () => {
      await loadLearningData(state, agentId);
      // Check for real (non-default) data — md files have content, json files differ from defaults.
      const hasRealData =
        Boolean(state.learningMemory?.trim()) ||
        Boolean(state.learningSoul?.trim()) ||
        Boolean(state.learningUser?.trim()) ||
        (state.learningEmotionalState !== DEFAULT_EMOTIONAL_STATE &&
          (state.learningEmotionalState as Record<string, unknown>)?.currentEmotion !==
            "neutral") ||
        ((state.learningTieredStats as Record<string, unknown>)?.totalMemories as number) > 0;
      if (hasRealData || state.learningInitializing !== checkKey) {
        state.learningInitializing = null;
      } else {
        pollForResults(state, agentId, checkKey, attempt + 1);
      }
    })();
  }, POLL_INTERVAL_MS);
}

export async function initLearningModule(
  state: LearningState,
  agentId: string,
  moduleKey: string,
): Promise<void> {
  if (!state.client || !state.connected) return;

  const prompt = MODULE_PROMPTS[moduleKey];
  if (!prompt) return;

  state.learningInitializing = moduleKey;
  try {
    const runId = generateUUID();
    await state.client.request("chat.send", {
      sessionKey: BACKGROUND_SESSION_KEY,
      message: prompt,
      deliver: false,
      idempotencyKey: runId,
    });

    // Poll until the agent creates the file or we time out.
    pollForResults(state, agentId, moduleKey);
  } catch (err) {
    state.learningError = `Failed to initialize ${moduleKey}: ${String(err)}`;
    state.learningInitializing = null;
  }
}

export async function initAllModules(state: LearningState, agentId: string): Promise<void> {
  if (!state.client || !state.connected) return;

  state.learningInitializing = "all";
  try {
    const runId = generateUUID();
    const allPrompts = Object.values(MODULE_PROMPTS).join("\n\n---\n\n");
    await state.client.request("chat.send", {
      sessionKey: BACKGROUND_SESSION_KEY,
      message: `อัพเดทไฟล์ระบบเรียนรู้ทั้งหมด ทำทีละอัน\n\n⚠️ กฎ: อ่านไฟล์เดิม → ต่อท้ายข้อมูลใหม่ → สรุปย่อทั้งไฟล์ให้กระชับที่สุด (ข้อมูลห้ามหาย)\n\n${allPrompts}`,
      deliver: false,
      idempotencyKey: runId,
    });

    // Poll until files appear or we time out.
    pollForResults(state, agentId, "all");
  } catch (err) {
    state.learningError = `Failed to initialize all modules: ${String(err)}`;
    state.learningInitializing = null;
  }
}
